import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { CheckIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { FunctionComponent } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Badge } from 'softcat:components/badge.tsx';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { BadgeState } from 'softcat:config/badge-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { ConfigService } from 'softcat:services/config.ts';
import { SoftcatApiPackage } from 'softcat:types/softcat-api.ts';
import { WidgetUtils } from 'softcat:utils/widget.ts';
import useSWR from 'swr';

type CatalogueAppItemProperties = {
    index: number;
    item: SoftcatApiPackage;
    name: string;
    remove: (index: number) => void;
};

export const CatalogueAppItem: FunctionComponent<CatalogueAppItemProperties> = ({ item, index, name, remove }) => {
    const { setValue, watch } = useFormContext();
    const { activeTerritory } = useTerritory();
    const isVisible = watch(`${name}.${index}.visibleInTray`);
    const selectedEntitlements = watch(`${name}.${index}.entitlements`) || [];

    const { data: platformsData = [] } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllPlatforms` }, ConfigService.findAllPlatforms);
    const { data: entitlementsData = [] } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllEntitlements` }, ConfigService.findAllEntitlements);

    return (
        <div className="flex-auto overflow-hidden">
            <div className="flex items-center gap-x-3">
                <EyeIcon
                    className={clsx('h-5 w-5 shrink-0 cursor-pointer', {
                        'text-gray-300': !isVisible,
                        'text-indigo-600': isVisible,
                    })}
                    data-testid={`toggle-visibility-${index}`}
                    onClick={() => {
                        setValue(`${name}.${index}.visibleInTray`, !isVisible);
                    }}
                />
                <div className={'shrink-0 text-xs'}>
                    <CertificateStatus certificateExpiry={item.widget?.publisherNotAfter} showText={false} />
                </div>
                <div className="w-full items-center space-x-2 truncate text-xs font-semibold leading-6 text-gray-900">
                    {item.widget?.name} <span className="text-gray-400">{item.widget?.version}</span>
                    {WidgetUtils.matchPlatforms({
                        platforms: platformsData,
                        widget: item.widget,
                    })?.map((platform) => {
                        return (
                            <Badge key={platform} status={BadgeState.INFO}>
                                {platform}
                            </Badge>
                        );
                    })}
                </div>
                <Listbox
                    multiple
                    onChange={(entitlements) => {
                        setValue(`${name}.${index}.entitlements`, entitlements);
                    }}
                    value={selectedEntitlements}
                >
                    <div className="relative">
                        <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-1 pl-3 pr-8 text-left text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:leading-6">
                            <span className="block truncate">
                                {selectedEntitlements.length} <FormattedMessage id={'CATALOGUES_EDIT_PAGE_ENTITLEMENTS_LABEL'} />
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
                            </span>
                        </ListboxButton>

                        <ListboxOptions
                            anchor={{ padding: 75, to: 'bottom end' }}
                            className="absolute z-10 mt-1 w-52 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                            transition
                        >
                            {entitlementsData.map((entitlement) => {
                                return (
                                    <ListboxOption
                                        className="group relative cursor-default select-none py-2 pl-3 pr-9 text-xs text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white"
                                        key={entitlement}
                                        value={entitlement}
                                    >
                                        <span className="block truncate font-normal group-data-[selected]:font-semibold">{entitlement}</span>
                                        <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-[selected]:flex group-data-[focus]:text-white">
                                            <CheckIcon aria-hidden="true" className="size-4" />
                                        </span>
                                    </ListboxOption>
                                );
                            })}
                        </ListboxOptions>
                    </div>
                </Listbox>
                <button
                    className={'shrink-0'}
                    data-testid={`remove-app-${index}`}
                    onClick={() => {
                        remove(index);
                    }}
                >
                    <TrashIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                </button>
            </div>
        </div>
    );
};
